import {
  useCallback,
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { useFetchCurrentUserSubscriptionsQuery } from '../redux/services/subscriptions';
import { useFetchSettingQuery } from '../../app/home/settings/redux-services';
import {
  getContextHomePath,
  getContextSlug,
} from './insight-context.utils';

const STORAGE_KEY_BASE = 'weelia:selected-context-agent-id';

export interface InsightContextItem {
  id: string;
  name: string;
  agentID: string;
  itemType?: string;
  status?: string;
  planName?: string;
}

interface InsightContextValue {
  contexts: InsightContextItem[];
  currentContext?: InsightContextItem;
  currentContextSlug?: string;
  currentContextHomePath?: string;
  currentContextAgentID: string | null;
  isCurrentContextSubscribed: boolean;
  isContextSubscribed: (agentID?: string | null) => boolean;
  setCurrentContextAgentID: Dispatch<SetStateAction<string | null>>;
  loadingContexts: boolean;
  refetchContexts: () => void;
}

const InsightContext = createContext<InsightContextValue>({
  contexts: [],
  currentContext: undefined,
  currentContextSlug: undefined,
  currentContextHomePath: undefined,
  currentContextAgentID: null,
  isCurrentContextSubscribed: false,
  isContextSubscribed: () => false,
  setCurrentContextAgentID: () => {},
  loadingContexts: false,
  refetchContexts: () => {},
});

const resolveStorageScope = (userInfo: any): string => {
  const app = String(userInfo?.app || 'weelia')
    .trim()
    .toLowerCase();
  const userRef = String(userInfo?.id || userInfo?._id || userInfo?.email || '')
    .trim()
    .toLowerCase();

  if (!userRef) {
    return STORAGE_KEY_BASE;
  }

  return `${STORAGE_KEY_BASE}:${app}:${userRef}`;
};

const getStoredAgentID = (storageKey: string): string | null => {
  if (typeof window === 'undefined') return null;
  return window.localStorage.getItem(storageKey);
};

const setStoredAgentID = (storageKey: string, agentID: string | null) => {
  if (typeof window === 'undefined') return;
  if (agentID) {
    window.localStorage.setItem(storageKey, agentID);
    return;
  }
  window.localStorage.removeItem(storageKey);
};

const isSubscriptionActive = (subscription: any): boolean => {
  const status = String(subscription?.status || '')
    .trim()
    .toLowerCase();
  return status === 'active' || status === 'trial' || status === 'trialing';
};

const toConfiguredContextList = (settingsData: any): InsightContextItem[] => {
  const rawContexts =
    settingsData?.apiConfig?.['weelia-service']?.contexts ||
    settingsData?.apiConfig?.weelia?.contexts ||
    [];

  const byAgentID = new Map<string, InsightContextItem>();

  for (const item of rawContexts || []) {
    const agentID = String(
      item?.agentID ??
        item?.agentId ??
        item?.itemID ??
        item?.itemId ??
        item?.id ??
        '',
    ).trim();
    if (!agentID) continue;

    const name =
      String(item?.name ?? item?.label ?? item?.title ?? '').trim() ||
      `Context ${agentID.slice(0, 8)}`;

    const candidate: InsightContextItem = {
      id: String(item?.id || agentID).trim() || agentID,
      name,
      agentID,
      itemType: String(item?.itemType || 'agent'),
      status: String(item?.status || 'active'),
      planName: item?.planName,
    };

    if (!byAgentID.has(agentID)) {
      byAgentID.set(agentID, candidate);
    }
  }

  return Array.from(byAgentID.values());
};

const toContextList = (subscriptions: any[]): InsightContextItem[] => {
  const byAgentID = new Map<string, InsightContextItem>();

  for (const subscription of subscriptions || []) {
    const agentID = String(subscription?.itemID || '').trim();
    if (!agentID) continue;

    const candidate: InsightContextItem = {
      id: subscription.id || agentID,
      name: subscription.itemName || `Context ${agentID.slice(0, 8)}`,
      agentID,
      itemType: subscription.itemType,
      status: subscription.status,
      planName: subscription?.plan?.name,
    };

    const existing = byAgentID.get(agentID);
    if (!existing) {
      byAgentID.set(agentID, candidate);
      continue;
    }

    if (
      existing.status !== 'active' &&
      candidate.status === 'active'
    ) {
      byAgentID.set(agentID, candidate);
    }
  }

  return Array.from(byAgentID.values()).sort((a, b) =>
    a.name.localeCompare(b.name),
  );
};

const mergeContextLists = (
  configuredContexts: InsightContextItem[],
  subscribedContexts: InsightContextItem[],
): InsightContextItem[] => {
  const byAgentID = new Map<string, InsightContextItem>();

  for (const context of configuredContexts) {
    byAgentID.set(context.agentID, context);
  }

  for (const subscriptionContext of subscribedContexts) {
    const existing = byAgentID.get(subscriptionContext.agentID);
    if (!existing) {
      byAgentID.set(subscriptionContext.agentID, subscriptionContext);
      continue;
    }

    const subscriptionHasPlaceholderName = /^Context\s+/i.test(
      subscriptionContext.name || '',
    );
    byAgentID.set(subscriptionContext.agentID, {
      ...existing,
      ...subscriptionContext,
      name: subscriptionHasPlaceholderName
        ? existing.name
        : subscriptionContext.name,
    });
  }

  return Array.from(byAgentID.values()).sort((a, b) =>
    a.name.localeCompare(b.name),
  );
};

export const InsightContextProvider = ({ children }: { children: ReactNode }) => {
  const userInfo = useSelector((state: any) => state.auth?.userInfo);
  const {
    data: subscriptionsData,
    isFetching: loadingSubscriptions,
    refetch: refetchSubscriptions,
  } = useFetchCurrentUserSubscriptionsQuery({});
  const {
    data: settingsData,
    isFetching: loadingSettings,
    refetch: refetchSettings,
  } = useFetchSettingQuery();

  const contexts = useMemo(() => {
    const configuredContexts = toConfiguredContextList(settingsData?.data);
    const subscribedContexts = toContextList(subscriptionsData?.data || []);
    return mergeContextLists(configuredContexts, subscribedContexts);
  }, [settingsData?.data, subscriptionsData?.data]);

  const subscribedContextAgentIDs = useMemo(() => {
    const agentIDs = (subscriptionsData?.data || [])
      .filter((subscription) => isSubscriptionActive(subscription))
      .map((subscription) => String(subscription?.itemID || '').trim())
      .filter(Boolean);

    return new Set(agentIDs);
  }, [subscriptionsData?.data]);

  const storageKey = useMemo(
    () => resolveStorageScope(userInfo),
    [userInfo?.id, userInfo?._id, userInfo?.email, userInfo?.app],
  );

  const [currentContextAgentID, setCurrentContextAgentID] = useState<
    string | null
  >(null);

  useEffect(() => {
    setCurrentContextAgentID(getStoredAgentID(storageKey));
  }, [storageKey]);

  useEffect(() => {
    if (!contexts.length) {
      if (currentContextAgentID) {
        setCurrentContextAgentID(null);
      }
      return;
    }

    const hasCurrentSelection = contexts.some(
      (item) => item.agentID === currentContextAgentID,
    );
    if (hasCurrentSelection) {
      return;
    }

    const storedAgentID = getStoredAgentID(storageKey);
    const storedContext = contexts.find((item) => item.agentID === storedAgentID);
    const activeContext = contexts.find((item) => item.status === 'active');
    const fallbackContext = activeContext || contexts[0];
    setCurrentContextAgentID(storedContext?.agentID || fallbackContext.agentID);
  }, [contexts, currentContextAgentID, storageKey]);

  useEffect(() => {
    setStoredAgentID(storageKey, currentContextAgentID);
  }, [storageKey, currentContextAgentID]);

  const currentContext = useMemo(
    () => contexts.find((item) => item.agentID === currentContextAgentID),
    [contexts, currentContextAgentID],
  );

  const isContextSubscribed = useCallback(
    (agentID?: string | null) =>
      Boolean(agentID && subscribedContextAgentIDs.has(String(agentID).trim())),
    [subscribedContextAgentIDs],
  );
  const isCurrentContextSubscribed = isContextSubscribed(currentContextAgentID);
  const currentContextSlug = currentContext
    ? getContextSlug(currentContext)
    : undefined;
  const currentContextHomePath = currentContext
    ? getContextHomePath(currentContext)
    : undefined;

  const loadingContexts = loadingSettings || loadingSubscriptions;
  const refetchContexts = () => {
    refetchSettings();
    refetchSubscriptions();
  };

  return (
    <InsightContext.Provider
      value={{
        contexts,
        currentContext,
        currentContextSlug,
        currentContextHomePath,
        currentContextAgentID,
        isCurrentContextSubscribed,
        isContextSubscribed,
        setCurrentContextAgentID,
        loadingContexts,
        refetchContexts,
      }}
    >
      {children}
    </InsightContext.Provider>
  );
};

export const useInsightContext = () => useContext(InsightContext);
